<template lang="html">
<div class="flow-duration-curve-chart">
    <div>
        <svg id="flow-duration-curve" :width="svgWidth" :height="svgHeight">
            <g class="chart" :transform="`translate(${margin.left},${margin.top})`"></g>
        </svg>
    </div>
    <chart-tooltip
    v-if="tooltipVisible"
    :top="tooltipPosition[1]"
    :left="tooltipPosition[0]"
    :content="tooltipText"
    />
</div>
</template>

<script>
import * as d3 from 'd3';
import numeral from 'numeral';

import ChartTooltip from '@/components/charts/ChartTooltip';
import sciNotation from '@/functions/sciNotationFmt';

export default {
    components: {
        ChartTooltip,
    },
    props: {
        // filtered data from crossfilter group.all()
        data: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            svgHeight: (window.innerHeight - 320) / 2,
            svgWidth: (window.innerWidth / 3.34) * 2,
            svg: null,
            g: null,
            margin: {
                top: 35,
                bottom: 35,
                left: 40,
                right: 50,
            },
            height: 225,
            width: 225,
            xScaleLinear: null, // literally just a linear distribution of 0-100 all the time.
            yScaleLog: null,
            barHeight: 4,
            axisLeft: null,
            initialized: false,
            line: null,
            focusCircle: null,
            // tooltip props
            tooltipPosition: [0, 0],
            tooltipVisible: false,
            tooltipText: [],
        };
    },
    mounted() {
        this.initializeSvg();
    },
    computed: {
        chartData() {
            return this.formatData(this.data);
        },
    },
    methods: {
        formatData(data) {
            // Don't show the chart if there are less than 100 points of data
            if (data.length < 100) {
                return [];
            }

            // Calculate quantiles 1-100% for the [filtered/sorted] data
            const flowDurations = [];

            for (let i = 0; i <= 100; i += 1) {
                const percent = i / 100;
                const quantile = d3.quantile(data, percent, d => d.v);
                flowDurations.push({
                    percent,
                    flow: quantile,
                });
            }

            return flowDurations;
        },
        initializeSvg() {
            // initialize chart & vars
            this.svg = d3.select('#flow-duration-curve');
            this.g = this.svg.select('g.chart');

            // chart (not element) size
            this.width = +this.svg.attr('width') - this.margin.left - this.margin.right;
            this.height = +this.svg.attr('height') - this.margin.top - this.margin.bottom;

            // initialize scale functions
            this.xScaleLinear = d3.scaleLinear().rangeRound([0, this.width]);
            this.yScaleLog = d3.scaleLog().rangeRound([this.height, 0]).clamp(true);

            // transition effects for the line
            this.transition = d3.transition().duration(500);

            // set x-scale input domain (giving a bit of margin)
            this.xScaleLinear.domain([-0.05, 1.05]);

            // bottom axis
            this.g.append('g')
            .attr('class', 'axis axis--x')
            .attr('transform', `translate(0,${this.height})`)
            .call(d3.axisBottom(this.xScaleLinear).tickFormat(d3.format('.0%')));
            // left axis
            this.axisLeft = d3.axisLeft(this.yScaleLog)
            .ticks(3)
            .tickFormat(sciNotation);
            this.g.append('g')
            .attr('class', 'axis axis--y')
            .attr('transform', 'translate(-2,0)');
            this.line = d3.line()
            .x(d => this.xScaleLinear(d.percent))
            .y(d => this.yScaleLog(d.flow));
            this.g.append('path')
            .attr('class', 'flow-duration-path')
            .attr('d', this.line(this.chartData));
            // define & update bars
            this.updateChart();

            // axis label
            this.g.append('text')
            .attr('class', 'axis-label')
            .attr('transform', 'translate(3,3) rotate(-90)')
            .attr('y', 6)
            .attr('dy', '0.71em')
            .attr('text-anchor', 'end')
            .text('Flow (m³/s)');

            // add mouseover focus circle
            this.focusCircle = this.g.append('circle')
            .attr('class', 'focus-circle')
            .attr('opacity', 0)
            .attr('r', 5);

            // add mouse move overlay
            const mouseArea = this.g.append('rect')
            .attr('class', 'mouse-area')
            .attr('height', this.height)
            .attr('width', this.width);

            // track mouse events on the chart area
            mouseArea.on('mousemove', this.mouseMove);
            mouseArea.on('mouseout', this.mouseOut);

            this.initialized = true;
        },
        updateChart() {
            this.yScaleLog.domain([
                0.001,
                d3.max(this.chartData, d => d.flow),
            ]).nice();

            this.g.select('.axis--y')
                .transition(this.transition)
                .call(this.axisLeft);

            this.g.select('.flow-duration-path')
                .transition(this.transition)
                .attr('d', this.line(this.chartData));
        },
        /**
         * update tooltip when the mouse moves
         */
        mouseMove() {
            // mouse coordinates within the g element
            const [gX] = d3.mouse(this.g.node());
            // x value at these mouse coordinates
            const valX = this.xScaleLinear.invert(gX);

            // hide on mouseout
            if (valX < 0 || valX > 1) {
                this.mouseOut();
                return;
            }

            // bisect the data using the array key (0-100)
            const i = Math.round(valX * 100);
            const xData = this.chartData[i];
            if (!xData) {
                return;
            }
            const lineY = this.yScaleLog(xData.flow);
            const valY = this.yScaleLog.invert(lineY);

            // move and show focus circle
            this.focusCircle.attr('opacity', 1)
            .attr('cy', lineY)
            .attr('cx', gX);

            // position relative to the root flow duration tool element
            this.tooltipPosition = d3.mouse(this.$parent.$el);

            const percentage = numeral(valX * 100).format('0');
            const flow = numeral(valY).format('0,0.[00]');
            this.tooltipText = [
                { text: `${percentage}% Exceedance Flow`, class: 'headline' },
                { text: `${flow} m³/s`, class: 'light-blue darken-4 white--text' },
            ];

            this.tooltipVisible = true;
        },
        /**
         * hide the tooltip on mouseout
         */
        mouseOut() {
            // hide focus circle
            this.focusCircle.attr('opacity', 0);

            // hide tooltip
            this.tooltipVisible = false;
        },
    },
    watch: {
        // update chart when data changes
        data: {
            handler() {
                this.updateChart();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.flow-duration-curve-chart {
    .flow-duration-path {
        fill: none;
        stroke-width: 2px;
        stroke: #01579B;
    }

    .focus-circle {
        fill: #EC407A;
    }

    .mouse-area {
        fill: none;
        pointer-events: all;
    }

    .axis-label {
        shape-rendering: optimizeLegibility;
        user-select: none;
        font-weight: 500;
        fill: #000;
        text-shadow: 0 0 1px #fff,
            -1px 0 1px #fff,
            1px 0 1px #fff;

    }

    // axis ticks
    g.axis {
        user-select: none;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/charts/FlowDurationCurve.vue